// ─── I-128 — LA ÚNICA cuenta de "cuánto es la mensualidad" ───────────────────
//
// 🔴 ARCHIVO ESPEJO. Existe DOS veces, byte a byte idéntico:
//      backend/src/helpers/amortizacion.ts      ← lo usará backend cuando I-123 migre
//                                                  crm.service.ts (BUG-1671/BUG-1672/BUG-346)
//      packages/shared/types/amortizacion.ts     ← lo usa el frontend vía '@erp/shared'
//    No se puede tener uno solo: el tsconfig del backend tiene `rootDir: ./src` y no
//    compila nada de fuera de src/. Mismo patrón que ya usa la casa para las fechas
//    de pago (`packages/shared/types/fechas.ts`) y el diccionario de ENUMs (§25.1).
//    🔴 CORREGIDO (I-133): aquí decía `amortizacion-una-sola-cuenta.test.ts`, que NO EXISTE
//    (medido, `find` en cero). El candado real vive en el frontend:
//    `frontend/src/__tests__/el-numero-que-ve-el-cliente.test.ts` — compara este archivo
//    contra su espejo y contra `crm.service.ts` (el oráculo del backend real).
//    Si editas uno, copia el otro. No los "adaptes" por separado.
//
// BUG-1671 — I-128, 2026-09-15: el cotizador embebible que el broker le enseña al
// cliente (`frontend/cotizador/data.jsx`) calculaba la mensualidad SIN redondear,
// mientras el backend, al generar el plan de pagos real, SÍ redondea a centavos
// (`generarPlanPagosFrancesa`, backend/src/services/crm.service.ts:2320-2323).
// El cliente veía un número y el sistema le cobraba otro.
//
// BUG-1672 — la misma fórmula estaba escrita a mano en 3 servicios del backend
// (crm.service.ts×3, proyectos.service.ts, planes_pago.service.ts), con DOS álgebras
// distintas que dan el mismo resultado redondeado (verificado numéricamente, I-128:
// diffs de 1e-11 en 5 casos — ruido de punto flotante, no divergencia real) y con
// una copia MUERTA sin llamador (crm.service.ts:4458, ya fichada BUG-346, 4-sep).
//
// ⛔ ESTE archivo es la ÚNICA fórmula que I-128 puede tocar hoy. Las 3 copias que
// viven dentro de `backend/src/services/crm.service.ts` (líneas 2322, 4304, 4439)
// son territorio de I-123 ("una sola fórmula del techo de comisión", mismo día,
// mismo archivo) — su migración a este helper es la Fase 2, no de esta pieza.
// `qa/dia-2026-09-15/verificar-una-sola-formula.sh` sale ROJO hoy por esas 3, a
// propósito: es el candado que impide que nazca una séptima copia sin que nadie
// se entere, no un defecto de esta pieza.

/** Redondeo a centavos — la única forma que usa esta casa: `Math.round(n*100)/100`.
 *  Es LITERALMENTE lo que hoy hace `round2` en `crm.service.ts:4125` (verificado,
 *  no supuesto) — no hay una segunda manera de redondear que unificar. */
export function redondear2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Tasa mensual a partir de la tasa anual. 0 si no hay interés (préstamo simple). */
export function calcularTasaMensual(tasaAnual: number): number {
  return tasaAnual > 0 ? tasaAnual / 100 / 12 : 0;
}

/**
 * BUG-1721 — LA fórmula del precio neto, para las dos puertas (cotizador del ERP,
 * `crm.service.ts::calcularPrecioNeto`, y el cotizador embebible, `cotizador/data.jsx`).
 *
 * UN SOLO redondeo — `round2(lista × (1 − pct/100))` — no dos. El cotizador embebible
 * redondeaba el DESCUENTO primero y el precio neto después (dos pasos), mientras el
 * backend siempre redondeó en un solo paso. Medido en 25,010 casos: hasta $0.01 de
 * diferencia en el precio que ve el cliente. Un centavo, y se ficha como un centavo —
 * pero es el MISMO patrón de dos puertas que BUG-1671 (I-128) ya cerró para la
 * mensualidad: una sola fórmula en el helper espejo, las dos puertas la llaman.
 */
export function calcularPrecioNeto(precioLista: number, descuentoPct = 0): number {
  const pct = Number.isFinite(descuentoPct) ? Math.min(Math.max(descuentoPct, 0), 100) : 0;
  return redondear2(precioLista * (1 - pct / 100));
}

/**
 * BUG-3885 (pantalla que resta) — el renglón «Descuento» de un contrato: `round2(lista − neto)`. La pantalla de generar contrato lo restaba a mano; aquí queda UNA, espejo del precio neto.
 */
export function calcularDescuentoDeLista(precioLista: number, precioNeto: number): number {
  return redondear2(precioLista - precioNeto);
}

/**
 * BUG-3885 (ataque a T3.4) — LA fórmula del enganche: `round2(monto × pct / 100)`, UN solo redondeo. Estaba escrita a mano en 4 sitios (`crm.routes.ts`, `crm.service.ts` ×2 y
 * `proyectos.service.ts`) con exactamente estas mismas operaciones, en este mismo orden: aquí queda UNA. El `pct` se redondea ANTES por quien llama (la columna es DECIMAL(5,2)).
 */
export function calcularEnganche(montoTotal: number, enganchePct: number): number {
  return redondear2(montoTotal * enganchePct / 100);
}

/**
 * LA fórmula — amortización francesa si hay tasa, saldo entre meses si no. Redondeada
 * a centavos, porque **el centavo es lo que el cliente firma y lo que el sistema cobra**.
 *
 * Replica EXACTO — mismo orden de operaciones, mismo redondeo — lo que
 * `generarPlanPagosFrancesa` hace hoy en `backend/src/services/crm.service.ts:2320-2323`
 * (la función que de verdad genera el plan de pagos de un contrato firmado). Si cambias
 * el orden de operaciones aquí, dejas de ser la misma cuenta que esa función — verifícalo
 * contra ella antes de tocar esta fórmula.
 */
export function calcularMensualidadBase(saldo: number, tasaAnual: number, plazoMeses: number): number {
  const tasaMensual = calcularTasaMensual(tasaAnual);
  if (tasaMensual > 0) {
    return redondear2(saldo * tasaMensual / (1 - Math.pow(1 + tasaMensual, -plazoMeses)));
  }
  return redondear2(saldo / plazoMeses);
}

/** Una fila de la corrida de amortización. */
export interface FilaAmortizacion {
  /** 1-indexado: 1 = primera mensualidad. */
  mes: number;
  /** Lo que se cobra ese mes. */
  monto: number;
  /** Saldo que queda DESPUÉS de aplicar `monto`, redondeado a centavos. */
  saldoDespues: number;
}

/**
 * La corrida completa de `plazoMeses` mensualidades sobre `saldo`. Mismo algoritmo que
 * el loop de `generarPlanPagosFrancesa` (crm.service.ts:2325-2343): todas las mensualidades
 * usan el mismo monto redondeado (`calcularMensualidadBase`) EXCEPTO la última, que absorbe
 * lo que quede de saldo — así el redondeo por centavos de cada mes no deja un residuo vivo
 * al final del plazo.
 *
 * 🔴 A propósito NO redondea `saldoRestante` en cada paso (igual que el original): sólo
 * redondea lo que se COBRA. Redondear el saldo intermedio sería una fórmula DISTINTA,
 * aunque se parezca — exactamente el error que esta pieza vino a cerrar en otro archivo.
 */
export function generarCorridaAmortizacion(
  saldo: number,
  tasaAnual: number,
  plazoMeses: number,
): FilaAmortizacion[] {
  const tasaMensual = calcularTasaMensual(tasaAnual);
  const mensualidadBase = calcularMensualidadBase(saldo, tasaAnual, plazoMeses);
  const filas: FilaAmortizacion[] = [];
  let saldoRestante = saldo;
  for (let i = 1; i <= plazoMeses; i++) {
    const esFinal = i === plazoMeses;
    // Lo que se debe ESTE mes (saldo + su interés). T3·3 (censo T3, 8-oct): una mensualidad nunca cobra MÁS que eso — con un saldo chico y un plazo largo la
    // mensualidad base redondeada a centavos (0.01) rebasaba el saldo, éste se hacía negativo y la última cuota salía NEGATIVA (−144.47). La última sigue absorbiendo el
    // redondeo (D-2026-09-07-T) y nunca se recorta a 0: se evita el sobrecobro antes de que llegue a ella. Donde el saldo es normal `debido` > base y nada cambia.
    const debido = tasaMensual > 0 ? saldoRestante * (1 + tasaMensual) : saldoRestante;
    let monto: number;
    if (esFinal) {
      monto = redondear2(Math.max(0, debido));
      saldoRestante = 0;
    } else {
      monto = Math.min(mensualidadBase, redondear2(Math.max(0, debido)));
      // Nunca por debajo de 0: redondear la mensualidad hacia arriba puede dejar el saldo en −0.005, y ese residuo negativo se arrastraba hasta la última cuota.
      saldoRestante = Math.max(0, tasaMensual > 0
        ? saldoRestante - (monto - saldoRestante * tasaMensual)
        : saldoRestante - monto);
    }
    filas.push({ mes: i, monto, saldoDespues: redondear2(Math.max(0, saldoRestante)) });
  }
  return filas;
}
